import { useRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import {
  useAddSalaryEmployeeAttachment,
  useDeleteSalaryEmployeeAttachment,
  useGetSalaryEmployeeAttachments,
} from "src/api/salary/employees";
import { notificationsState } from "src/store/notifications";
import { APIErrorResponse } from "src/types/generic";
import AttachmentsDialog from "src/shared/components/attachments-dialog";

interface Props {
  open: boolean;
  onClose: () => void;
  employeeId: number | null;
  employeeName?: string;
}

const AttachmentsPopup = ({
  open,
  onClose,
  employeeId,
  employeeName,
}: Props) => {
  const { t: generalT } = useTranslation("general");
  const [notifications, setNotifications] = useRecoilState(notificationsState);

  // APIs
  const {
    data: attachmentsRes,
    isLoading,
    refetch,
  } = useGetSalaryEmployeeAttachments(employeeId, open && !!employeeId);
  const data = attachmentsRes?.data ?? [];

  const { mutate: upload, isPending: uploading } =
    useAddSalaryEmployeeAttachment();
  const { mutate: remove, isPending: removing } =
    useDeleteSalaryEmployeeAttachment();

  return (
    <AttachmentsDialog
      open={open}
      onClose={onClose}
      entityId={employeeId}
      entityLabel={employeeName}
      attachments={data}
      isLoading={isLoading}
      refetch={refetch}
      uploading={uploading}
      removing={removing}
      onUpload={(file) => {
        if (!employeeId) return;
        upload(
          { employeeId, file },
          {
            onSuccess: () => {
              refetch();
              setNotifications([
                ...notifications,
                { type: "success", message: generalT("addedSuccessfully") },
              ]);
            },
            onError: (err: APIErrorResponse) => {
              setNotifications([
                ...notifications,
                {
                  type: "error",
                  message:
                    err?.data?.errors?.[0]?.value ?? generalT("somethingWrong"),
                },
              ]);
            },
          },
        );
      }}
      onRemove={(attachmentId, callbacks) => {
        if (!employeeId) return;
        remove(
          { employeeId, employeeFileId: attachmentId },
          callbacks,
        );
      }}
    />
  );
};

export default AttachmentsPopup;
